//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect } from 'react';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';

// import { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import SimpleBar from 'simplebar-react';
// import FlipCountdown from '@rumess/react-flip-countdown';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { logPageView } from '../system/firebase';
import { LayoutContext, AppContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import {
    ADVERTISE_PAGE_ID_INTRO,
    ADVERTISE_PAGE_ID_START,
    ADVERTISE_PAGE_ID_STEP_1,
    ADVERTISE_PAGE_ID_STEP_2,
    ADVERTISE_PAGE_ID_STEP_3,
    ADVERTISE_PAGE_ID_STEP_4,
    ADVERTISE_PAGE_ID_STEP_5,
    ADVERTISE_PAGE_ID_SUCCESS,
    BASE_URL,
    DASHBOARD_ID_MY_CALENDAR,
    PAGE_TITLE_ADVERTISE_WITH_US,
} from '../common/constants';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import { PATH_ADVERTISE, STRIPE_API_PUBLISHABLE_KEY, PATH_DASHBOARD } from '../system/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';
import TopNav from '../topNav/TopNav';
import AfitdMessageDialog from '../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import IntroPage from './subComponents/IntroPage';
import StartPage from './subComponents/StartPage';
import Step1Page from './subComponents/Step1Page';
import Step2Page from './subComponents/Step2Page';
import Step3Page from './subComponents/Step3Page';
import Step4Page from './subComponents/Step4Page';
import Step5Page from './subComponents/Step5Page';
import SuccessPage from './subComponents/SuccessPage';
import Footer from '../home/subComponents/Footer';
import PageMessage from '../common/elemental/PageMessage';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    pageTopNav: { maxHeight: '81px' },
    pageContent: { flex: 1, overflowY: 'hidden' },
    counterDiv: {
        position: 'fixed',
        right: '30px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, showExpireModal, handleCloseUnavailablePlacementDialog } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageTopNav: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },
        unavailablePlacementDialog: {
            title: 'Session Expired!',
            children: <AfitdTypography>Sorry, the session has expired, please go back to check if the your desired placement is still available.</AfitdTypography>,
            open: showExpireModal,
            onClose: handleCloseUnavailablePlacementDialog,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_API_PUBLISHABLE_KEY);

// const TODO______Remove_hard_coded_code = null;
const initCustomerData = {
    city: '',
    companyName: '',
    country: '',
    email: '',
    phone: '',
    poc: '',
    state: '',
};

const getPageComponent = pageId => {
    switch (pageId) {
        case ADVERTISE_PAGE_ID_INTRO:
            return <IntroPage />;
        case ADVERTISE_PAGE_ID_START:
            return <StartPage />;
        case ADVERTISE_PAGE_ID_STEP_1:
            return <Step1Page />;
        case ADVERTISE_PAGE_ID_STEP_2:
            return <Step2Page />;
        case ADVERTISE_PAGE_ID_STEP_3:
            return <Step3Page />;
        case ADVERTISE_PAGE_ID_STEP_4:
            return <Step4Page />;
        case ADVERTISE_PAGE_ID_STEP_5:
            return <Step5Page />;
        case ADVERTISE_PAGE_ID_SUCCESS:
            return <SuccessPage />;
        default:
            return <IntroPage />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AdvertiseLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();

    const appContext = useContext(AppContext);
    const { currentUser } = appContext;

    const context = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = context;
    const { pageId } = useParams();

    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { isShowJobs } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [customerData, setCustomerData] = React.useState(initCustomerData);
    const [adImageUrl, setAdImageUrl] = React.useState(null);
    const [paymentIntentDescription, setPaymentIntentDescription] = React.useState('');

    const [showExpireModal, setShowExpireModal] = React.useState(false);

    const [checkState, setCheckState] = React.useState({
        jobCtr: false,
        talentCtr: false,
        empSearch: false,
    });

    const [checkedAvailability, setCheckedAvailability] = React.useState({
        jobCtr: false,
        talentCtr: false,
        empSearch: false,
    });

    const [hideJobCenterList, setHideJobCenterList] = React.useState(true);
    const [hideTalentCenterList, setHideTalentCenterList] = React.useState(true);
    const [hideEmpSearchList, setHideEmpSearchList] = React.useState(true);

    const [jobCenterAdDetails, setJobCenterAdDetails] = React.useState({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
    const [talentCenterAdDetails, setTalentCenterAdDetails] = React.useState({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
    const [employerSearchAdDetails, setEmployerSearchAdDetails] = React.useState({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });

    const [jobCenterPlacementData, setJobCenterPlacementData] = React.useState({});
    const [talentCenterPlacementData, setTalentCenterPlacementData] = React.useState({});
    const [empSearchPlacementData, setEmpSearchPlacementData] = React.useState({});

    const [showTimer, setShowTimer] = React.useState(false);

    const [onHoldDocIds, setOnHoldDocIds] = React.useState([]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_ADVERTISE_WITH_US);
    }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleCloseUnavailablePlacementDialog = () => {
        history.push(`${BASE_URL}${PATH_ADVERTISE}/step3`);

        setShowExpireModal(false);

        setShowTimer(false);

        setCheckedAvailability({
            jobCtr: false,
            talentCtr: false,
            empSearch: false,
        });
        setJobCenterAdDetails({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
        setJobCenterPlacementData({});

        setTalentCenterAdDetails({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
        setTalentCenterPlacementData({});

        setEmployerSearchAdDetails({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
        setEmpSearchPlacementData({});
    };

    const handleClickBackToHome = () => {
        if (currentUser) history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_CALENDAR}`);
        else history.push(`/`);

        setCustomerData(initCustomerData);
        setShowExpireModal(false);

        setShowTimer(false);

        setCheckedAvailability({
            jobCtr: false,
            talentCtr: false,
            empSearch: false,
        });
        setJobCenterAdDetails({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
        setJobCenterPlacementData({});

        setTalentCenterAdDetails({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
        setTalentCenterPlacementData({});

        setEmployerSearchAdDetails({ placement: '', date: null, time: new Date().setHours(24, 0, 0, 0) });
        setEmpSearchPlacementData({});
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, showExpireModal, handleCloseUnavailablePlacementDialog, handleClickBackToHome });

    // const onHoldEndDt = localStorage.getItem('onHoldEndDt');

    //---------------------------------------------------------------------
    //TODO Remove this when ready to introduce Advertising
    //TODO Remove this when ready to introduce Advertising
    //TODO Remove this when ready to introduce Advertising
    //TODO Remove this when ready to introduce Advertising
    //---------------------------------------------------------------------
    if (process.env.REACT_APP_ENV === 'PROD')
        return (
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageTopNav}>
                    <TopNav />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ height: '100%', width: '100%', flex: 1 }} ref={pageContentSimpleBarEl}>
                        <PageMessage userNoAccess />
                    </SimpleBar>
                </Grid>
            </Grid>
        );
    //---------------------------------------------------------------------
    //TODO END OF: Remove this when ready to introduce Advertising
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...context,
                scrollToPageTop,
                // Step1
                customerData,
                setCustomerData,
                // Step2
                adImageUrl,
                setAdImageUrl,
                // Step3
                checkState,
                setCheckState,
                checkedAvailability,
                setCheckedAvailability,
                hideJobCenterList,
                setHideJobCenterList,
                hideTalentCenterList,
                setHideTalentCenterList,
                hideEmpSearchList,
                setHideEmpSearchList,
                jobCenterAdDetails,
                setJobCenterAdDetails,
                talentCenterAdDetails,
                setTalentCenterAdDetails,
                employerSearchAdDetails,
                setEmployerSearchAdDetails,
                jobCenterPlacementData,
                setJobCenterPlacementData,
                talentCenterPlacementData,
                setTalentCenterPlacementData,
                empSearchPlacementData,
                setEmpSearchPlacementData,
                setShowTimer,
                onHoldDocIds,
                setOnHoldDocIds,
                paymentIntentDescription,
                setPaymentIntentDescription,
                handleClickBackToHome,
            }}
        >
            <AfitdPageHead title={PAGE_TITLE_ADVERTISE_WITH_US} />
            <Elements stripe={stripePromise}>
                <Grid {...defns.pageContainer}>
                    <Grid {...defns.pageTopNav}>
                        <TopNav />
                    </Grid>
                    <Grid {...defns.pageContent}>
                        <SimpleBar style={{ height: '100%', width: '100%', flex: 1 }} ref={pageContentSimpleBarEl}>
                            {showTimer && (
                                <div {...defns.counterDiv}>
                                    {/* <FlipCountdown
                                        endAt={onHoldEndDt} // Date/Time
                                        hideYear
                                        hideMonth
                                        hideDay
                                        hideHour
                                        titlePosition="bottom"
                                        endAtZero
                                        onTimeUp={() => {
                                            localStorage.removeItem('onHoldEndDt');
                                            setShowExpireModal(true);
                                            setShowTimer(false);
                                        }}
                                    /> */}
                                </div>
                            )}
                            <Grid {...defns.tempContainer}>
                                {getPageComponent(pageId)}
                                <br />
                                <br />
                                <br />
                            </Grid>
                            <AfitdMessageDialog {...defns.unavailablePlacementDialog} />
                        </SimpleBar>
                    </Grid>
                </Grid>
                <Footer />
            </Elements>
        </LayoutContext.Provider>
    );
};

export default AdvertiseLayout;
